import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Globe, ChevronLeft } from 'lucide-react';

const TOWER_NAMES = { kirei1: 'Kirei I', kirei2: 'Kirei II' };

export default function Navbar({ transparent }) {
  const { t, toggleLang, lang } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const isTowerPage = location.pathname.startsWith('/tower/');
  const isDarkPage = isHome || isTowerPage;
  const isTransparent = transparent || isDarkPage;

  // Extract tower ID from URL if on a tower page
  const towerMatch = location.pathname.match(/\/tower\/(\w+)/);
  const currentTower = towerMatch ? towerMatch[1] : null;

  const handleExploreClick = () => {
    if (isTowerPage && currentTower) {
      // Scroll to building explorer
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 ${
        isTransparent
          ? 'bg-black/20 backdrop-blur-md border-b border-white/5'
          : 'bg-white/90 backdrop-blur-xl border-b border-[#E5E5E5]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Left: Logo + Tower indicator */}
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <span
              className="font-light tracking-[0.15em] text-lg md:text-xl"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#D4AF37' }}
            >
              KIREI
            </span>
          </Link>
          {currentTower && (
            <>
              <span className="text-white/20 text-sm">/</span>
              <span
                className="text-xs tracking-[0.1em] uppercase"
                style={{ fontFamily: "'Outfit', sans-serif", color: isTransparent ? 'rgba(255,255,255,0.6)' : '#8A828F' }}
                data-testid="nav-tower-name"
              >
                {TOWER_NAMES[currentTower] || currentTower}
              </span>
            </>
          )}
        </div>

        {/* Right: Navigation */}
        <div className="flex items-center gap-4 md:gap-8">
          {/* Back to towers (when on tower page) */}
          {currentTower && (
            <Link
              to="/"
              data-testid="nav-towers"
              className={`flex items-center gap-1 text-xs md:text-sm font-light tracking-[0.1em] uppercase transition-colors duration-300 hover:text-[#D4AF37] ${
                isTransparent ? 'text-white/70' : 'text-[#8A828F]'
              }`}
            >
              <ChevronLeft size={14} />
              {lang === 'es' ? 'Torres' : 'Towers'}
            </Link>
          )}

          {isTowerPage && (
            <button
              onClick={handleExploreClick}
              data-testid="nav-explore"
              className={`text-xs md:text-sm font-light tracking-[0.1em] uppercase transition-colors duration-300 hover:text-[#D4AF37] cursor-pointer ${
                isTransparent ? 'text-white/70' : 'text-[#8A828F]'
              }`}
            >
              {t('nav.explore')}
            </button>
          )}

          <Link
            to="/admin"
            data-testid="nav-admin"
            className={`text-xs md:text-sm font-light tracking-[0.1em] uppercase transition-colors duration-300 hover:text-[#D4AF37] ${
              isTransparent ? 'text-white/70' : 'text-[#8A828F]'
            }`}
          >
            {t('nav.admin')}
          </Link>
          {user?.role === 'super_admin' && (
            <Link
              to="/admin/cms"
              data-testid="nav-cms"
              className={`text-xs md:text-sm font-light tracking-[0.1em] uppercase transition-colors duration-300 hover:text-[#D4AF37] ${
                isTransparent ? 'text-white/70' : 'text-[#8A828F]'
              }`}
            >
              {t('nav.cms')}
            </Link>
          )}
          <button
            onClick={toggleLang}
            data-testid="language-toggle"
            className={`flex items-center gap-1.5 text-xs md:text-sm font-light tracking-[0.1em] uppercase transition-colors duration-300 hover:text-[#D4AF37] ${
              isTransparent ? 'text-white/70' : 'text-[#8A828F]'
            }`}
          >
            <Globe size={14} />
            {lang === 'es' ? 'EN' : 'ES'}
          </button>
        </div>
      </div>
    </nav>
  );
}
